import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { InputLabel, Stack, Grid } from '@mui/material';
import React, { memo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ActionIconButton from '../common/ActionIconButton';
import { useModelForm } from '../../contexts/ModelForm';
import { usePrerequisites } from '../../contexts/Prerequisites';
import { useDeepCallback } from '../../hooks';

const evalOperands = ({ key, column, formik, activeRecord, defaultValue = false }) => {
	const checkOperandValue = (check) => {
		let field, op, controlValue;
		let evaluatedVal = defaultValue;
		if (Array.isArray(check)) {
			field = check[0];
			controlValue = check[1];
		} else if (typeof check === 'object') {
			field = check.field;
			controlValue = check.value;
			op = check.op || check.operand || 'eq';
		}
		const value = formik.values[field];
		if (typeof controlValue === 'function') {
			controlValue = controlValue({ value: value, formik, activeRecord });
		}
		if (typeof op === 'function') {
			evaluatedVal = op({ value: value, values: formik.values, activeRecord });
		}
		else {
			op = op?.trim?.()?.toLowerCase?.() || 'eq';
			switch (op) {
				case 'ne':
				case 'notequal':
					evaluatedVal = controlValue !== value;
					break;
				case 'in':
				case 'includes':
				case 'has':
					if (Array.isArray(controlValue)) {
						evaluatedVal = controlValue.indexOf(value) !== -1;
					} else if (typeof controlValue === 'object') {
						evaluatedVal =
							typeof controlValue.has === 'function' ? controlValue.has(value) : value in controlValue;
					} else if (typeof controlValue === 'string') {
						evaluatedVal = controlValue.includes(value);
					}
					break;
				case 'nin':
				case 'notin':
					if (Array.isArray(controlValue)) {
						evaluatedVal = controlValue.indexOf(value) === -1;
					} else if (typeof controlValue === 'object') {
						evaluatedVal =
							typeof controlValue.has === 'function' ? !controlValue.has(value) : !(value in controlValue);
					} else if (typeof controlValue === 'string') {
						evaluatedVal = !controlValue.includes(value);
					}
					break;
				case 'lt':
				case 'lessthan':
					evaluatedVal = value < controlValue;
					break;
				case 'lte':
				case 'lessthanorequal':
					evaluatedVal = value <= controlValue;
					break;
				case 'gt':
				case 'greaterthan':
					evaluatedVal = value > controlValue;
					break;
				case 'gte':
				case 'greaterthanorequal':
					evaluatedVal = value >= controlValue;
					break;
				case 'or':
					evaluatedVal =
						evaluatedVal || Array.isArray(controlValue) ? controlValue.some(checkOperandValue) : evaluatedVal;
					break;
				case 'and':
					evaluatedVal = Array.isArray(controlValue) ? controlValue.every(checkOperandValue) : evaluatedVal;
					break;
				default:
					evaluatedVal = value === controlValue;
					break;
			}
		}

		return evaluatedVal;
	};
	return typeof column === 'object' && key in column && Array.isArray(column[key])
		? column[key].every(checkOperandValue)
		: defaultValue;
};

const evalIsFieldProp = ({ prop, field, column, activeRecord, validationSchema, formik, defaultValue = false }) => {
	let isFieldProp = defaultValue;
	if (prop in column) {
		switch (typeof column[prop]) {
			case 'function':
				isFieldProp = column[prop]({ field, column, activeRecord, formik, validationSchema });
				if (prop === 'required' && Array.isArray(isFieldProp)) {
					isFieldProp = evalOperands({
						formik,
						column: { ...column, [prop]: isFieldProp },
						key: prop,
						activeRecord,
						defaultValue
					});
				}
				break;
			case 'boolean':
				isFieldProp = column[prop];
				break;
			case 'object':
				if (Array.isArray(column[prop])) {
					isFieldProp = evalOperands({ formik, column, key: prop, activeRecord, defaultValue });
				} else {
					isFieldProp = column[prop];
				}
				break;
			default:
				isFieldProp = Boolean(column[prop]);
				break;
		}
	}

	return isFieldProp;
};

const evalFieldProps = ({ field, fieldLabel, column, getFormik, activeRecord, validationSchema, navigate, inlineLabel }) => {
	const formik = getFormik();
	const disabled = Array.isArray(column.disabled)
		? evalOperands({ formik, column, activeRecord, key: 'disabled', defaultValue: false })
		: typeof column.disabled === 'function'
			? column.disabled({ formik, navigate, activeRecord, fieldLabel, column })
			: Boolean(column.disabled);
	const readOnly = evalIsFieldProp({ field, column, activeRecord, validationSchema, formik, prop: 'readOnly' });

	const required = evalIsFieldProp({ field, column, activeRecord, validationSchema, formik, prop: 'required' });
	const visible = evalIsFieldProp({
		field,
		column,
		activeRecord,
		validationSchema,
		formik,
		prop: 'inputWhen',
		defaultValue: true
	});

	const addable =
		typeof column.addable === 'function' ? column.addable({ formik, activeRecord, navigate }) : column.addable;
	const editable =
		typeof column.editable === 'function' ? column.editable({ formik, activeRecord, navigate }) : column.editable;
	const typeDependentProps = {};
	if (typeof column.type === 'string') {
		switch (column.type.toLowerCase()) {
			case 'date':
			case 'datetime':
				if ('maxDate' in column) {
					typeDependentProps.maxDate = evalIsFieldProp({
						field,
						column,
						activeRecord,
						validationSchema,
						formik,
						prop: 'maxDate'
					});
				}
				if ('maxTime' in column) {
					typeDependentProps.maxTime = evalIsFieldProp({
						field,
						column,
						activeRecord,
						validationSchema,
						formik,
						prop: 'maxTime'
					});
				}
				if ('minDate' in column) {
					typeDependentProps.minDate = evalIsFieldProp({
						field,
						column,
						activeRecord,
						validationSchema,
						formik,
						prop: 'minDate'
					});
				}
				if ('minTime' in column) {
					typeDependentProps.minTime = evalIsFieldProp({
						field,
						column,
						activeRecord,
						validationSchema,
						formik,
						prop: 'minTime'
					});
				}
				if ('timeFormat' in column) {
					typeDependentProps.timeFormat = evalIsFieldProp({
						field,
						column,
						activeRecord,
						validationSchema,
						formik,
						prop: 'timeFormat'
					});
				}
				break;
			case 'time':
				if ('maxTime' in column) {
					typeDependentProps.maxTime = evalIsFieldProp({
						field,
						column,
						activeRecord,
						validationSchema,
						formik,
						prop: 'maxTime'
					});
				}
				if ('minTime' in column) {
					typeDependentProps.minTime = evalIsFieldProp({
						field,
						column,
						activeRecord,
						validationSchema,
						formik,
						prop: 'minTime'
					});
				}
				break;
			default:
				break;
		}
	}
	inlineLabel = 'inlineLabel' in column ? column.inlineLabel : inlineLabel || typeof inlineLabel === 'undefined';
	const inlineLabelSize = column?.inlineLabelSize > 0 && column?.inlineLabelSize <= 12 ? column.inlineLabelSize : 2;
	const inlineInputSize = inlineLabelSize < 12 ? 12 - inlineLabelSize : 12;
	return {
		readOnly,
		disabled,
		required,
		visible,
		addable,
		editable,
		inlineLabelSize,
		inlineInputSize,
		inlineLabel,
		...typeDependentProps
	};
};
const InputOrnaments = memo(
	({ field, addable, editable, disabled, readOnly, inputSiblingComponents, formik, navigate, activeRecord }) => (
		<Stack
			direction={'row'}
			spacing={1} 
			alignItems="flex-start"
			sx={{
				margin: (theme) => theme.spacing(0.4, 0, 0, 1),
				'& .MuiButton-root': { p: (theme) => theme.spacing(1.25, 0.5, 1.25) },
				'& .MuiIconButton-root': { borderRadius: '5px' }
			}}
		>
			{(addable || editable) && (
				<ActionIconButton
					color="success"
					variant="contained"
					className={addable ? 'addableOrnament' : 'editableOrnament'}
					component={Link}
					to={Boolean(formik.values[field]) && editable ? editable : addable}
					disabled={disabled}
				>
					{Boolean(formik.values[field]) && editable && <EditIcon fontSize="inherit" />}
					{(!editable || (!formik.values[field] && addable)) && <AddIcon fontSize="inherit" />}
				</ActionIconButton>
			)}
			{inputSiblingComponents &&
				inputSiblingComponents.map((InputSiblingComponent, i) => (
					<InputSiblingComponent
						formik={formik}
						navigate={navigate}
						activeRecord={activeRecord}
						disabled={disabled}
						readOnly={readOnly}
						key={`input-action-${i}`}
					/>
				))}
		</Stack>
	)
);

const InputWrapper = memo(({ Input, field, fieldLabel, formGroupIndex, column, xs, md, size, ...rest }) => {
	const navigate = useNavigate();
	const { formik, activeRecord, initialValues, evalFieldValidationError, getFormik } = useModelForm();

	const [{ strings }] = usePrerequisites();
	const inputLabel = strings[column?.fieldLabelName] || column?.fieldLabelName || fieldLabel;

	const errors = formik.errors;
	const {
		required,
		visible,
		readOnly,
		disabled,
		inlineLabel,
		inlineLabelSize,
		inlineInputSize,
		addable,
		editable,
		...otherProps
	} = evalFieldProps({
		field,
		fieldLabel,
		getFormik,
		column,
		activeRecord,
		...rest
	});

	const CustomizedInput = column?.customizeInput;
	const fieldError = errors?.[field];

	const error =
		visible && typeof evalFieldValidationError === 'function'
			? evalFieldValidationError({ field, message: fieldError, touch: false, formGroupIndex })
			: null;


	const applyChangeEffects = useDeepCallback(
		(value) => {
			if (typeof column.onChange === 'function') {
				const formikBag = getFormik?.() || formik;
				column.onChange({
					formik: formikBag,
					value,
					values: { ...formikBag?.values, [field]: value },
					activeRecord,
					field
				});
			}
		},
		[activeRecord, formik]
	);

	const applyBlurEffects = useDeepCallback(
		(event, value) => {
			if (typeof column.onBlur === 'function') {
				const formikBag = typeof getFormik === 'function' ? getFormik() : formik;
				column.onBlur(event, {
					formik: formikBag,
					value,
					values: { ...formikBag?.values, [field]: value },
					activeRecord,
					field
				});
			}
		},
		[activeRecord, formik]
	);

	const setFieldValue = useDeepCallback(
		(fieldName, value, revalidate = false, touched = undefined) => {
			if (typeof touched === 'boolean') {
				formik.setFieldTouched(fieldName, touched); // field touched comes first
			}
			formik.setFieldValue(fieldName, value, revalidate);
			applyChangeEffects(value);
		},

		[formik]
	);

	const handleOnChange = useCallback(
		(newValue, revalidate = true, touched = undefined) => {
			const target = newValue?.target || newValue?.currentTarget;
			let value = newValue;
			if (target) {
				value = typeof target === 'object' && 'checked' in target ? target.checked : target?.value;
				formik.handleChange(newValue);
			} else {
				formik.setFieldValue(field, value, revalidate);
			}
			if (typeof touched === 'boolean') {
				setTimeout(() => formik.setFieldTouched(field, touched), 50);
			}
			applyChangeEffects(value);
		},
		[]
	);

	const handleOnBlur = useCallback(
		(event) => {
			formik.handleBlur(event);
			const value = event?.target?.value;
			applyBlurEffects(event, value);
		},
		[]
	);

	if (!visible) {
		return;
	}
	const hasInlineLabel = inlineLabel || typeof inlineLabel === 'undefined';
	const inputColumns = (hasInlineLabel && !column.hideLabel ? inlineInputSize : 12);
	return (
		<Grid
			key={field}
			size={{ xs: 12, md: 12, ...size }}
			padding={0}
			{...column.inputWrapperProps}
			className={`inputWrapper ${column?.inputWrapperProps?.className ?? ''}`}
			data-input-name={column.name}
		>
			<Grid
				container
				rowGap={'10px'}
				{...column.innerInputWrapperProps}
				className={`innerInputWrapper ${column?.innerInputWrapperProps?.className ?? ''}`}
			>
				{hasInlineLabel && !column.hideLabel && (
					<Grid
						size={{ xs: 12, lg: inlineLabelSize }}
						sx={{ alignItems: 'center', display: 'flex' }}
						item
						className="inlineLabelContainer"
						{...column.labelContainerProps}
					>
						<InputLabel required={required} error={!!error}>{inputLabel}</InputLabel>
					</Grid>
				)}

				<Grid size={{xs: 12, lg: inputColumns}} className="inlineInputContainer" padding={0} sx={{ alignItems: 'center', display: 'flex' }} item {...column.inputContainerProps}>
					<Stack
						direction="row"
						className="inputContainer"
						alignItems="flex-start"
						sx={{ width: '100%' }}
					>
						{CustomizedInput ? (
							<CustomizedInput
								Input={Input}
								inputProps={{
									...rest,
									...otherProps,
									field,
									column,
									fieldLabel: inputLabel,
									activeRecord,
									inlineLabel,
									formik: formik,
									setFieldValue,
									onChange: handleOnChange,
									onBlur: handleOnBlur,
									readOnly,
									disabled,
									error
								}}
							/>
						) : (
							<Input
								{...otherProps}
								{...rest}
								fieldLabel={inputLabel}
								field={field}
								column={column}
								activeRecord={activeRecord}
								inlineLabel={inlineLabel}
								formik={formik}
								setFieldValue={setFieldValue}
								onChange={handleOnChange}
								onBlur={handleOnBlur}
								readOnly={readOnly}
								disabled={disabled}
								error={error}
							/>
						)}
						{(column.inputSiblingComponents?.length > 0 || !!addable || !!editable) && (
							<InputOrnaments
								addable={addable}
								editable={editable}
								inputSiblingComponents={column.inputSiblingComponents}
								navigate={navigate}
								formik={formik}
								activeRecord={activeRecord}
								initialValues={initialValues}
								field={field}
								fieldLabel={inputLabel}
								disabled={disabled}
								readOnly={readOnly}
							/>
						)}
					</Stack>
				</Grid>
			</Grid>
		</Grid>
	);
});
export const withInputWrapper = (Input) => {
	const wrapInput =
		(Input) =>
			({ children, ...props }) =>
			(
				<InputWrapper Input={Input} {...props}>
					{children}
				</InputWrapper>
			);
	return wrapInput(Input);
};
export default InputWrapper;
